/* eslint-disable no-restricted-syntax */
// Deprecated: kept for backward compatibility

/**
 * Theme breakpoint scale — single source of truth for JavaScript viewport checks.
 *
 * These values mirror the CSS utility class breakpoints in grid.css,
 * utilities.css, and spacing.css. CSS @media queries cannot reference these
 * values directly — if you change a breakpoint here, update those files too.
 *
 *   sm  (576px)  → .col-sm-*   .d-sm-*   .m-sm-*
 *   md  (768px)  → .col-md-*   .d-md-*   .m-md-*
 *   lg  (992px)  → .col-lg-*   .d-lg-*   .m-lg-*  ← mobile/desktop split
 *   xl  (1200px) → .col-xl-*   .d-xl-*   .m-xl-*
 *   xxl (1400px) → .col-xxl-*  .d-xxl-*  .m-xxl-*
 */
const BREAKPOINTS = {
  sm: 576,
  md: 768,
  lg: 992,
  xl: 1200,
  xxl: 1400,
  ipadProMin: 1024,
  ipadProMax: 1366,
};

const TRAP_FOCUS_HANDLERS = {};

const Utility = {
  BREAKPOINTS,
  _swatchesCache: null,

  /**
   * DOM Selection Utilities
   * Simplified methods for querying DOM elements
   */
  DomSelector: {
    /**
     * Query single element with optional parent context
     * @param {string} selector - CSS selector
     * @param {Element} context - Parent element (default: document)
     * @returns {Element|null}
     */
    $(selector, context = document) {
      return (context || document).querySelector(selector);
    },

    /**
     * Query multiple elements with optional parent context
     * @param {string} selector - CSS selector
     * @param {Element} context - Parent element (default: document)
     * @returns {Array}
     */
    $$(selector, context = document) {
      return Array.from((context || document).querySelectorAll(selector));
    },

    /**
     * Query element by ID
     * @param {string} id - Element ID (without #)
     * @returns {Element|null}
     */
    byId(id) {
      return document.getElementById(id);
    },

    /**
     * Check if element exists
     * @param {string} selector
     * @param {Element} context
     * @returns {boolean}
     */
    exists(selector, context = document) {
      return (context || document).querySelector(selector) !== null;
    },
  },

  /**
   * CSS Class Manipulation Utilities
   * Simplified methods for managing CSS classes on single or multiple elements
   */
  CSS: {
    /**
     * Add class to single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {...string} classes - Classes to add
     */
    add(elements, ...classes) {
      if (!elements || classes.length === 0) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach((el) => el?.classList.add(...classes));
    },

    /**
     * Remove class from single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {...string} classes - Classes to remove
     */
    remove(elements, ...classes) {
      if (!elements || classes.length === 0) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach((el) => el?.classList.remove(...classes));
    },

    /**
     * Toggle class on single or multiple elements
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {string} className - Class to toggle
     * @param {boolean} force - Optional: force add (true) or remove (false)
     */
    toggle(elements, className, force) {
      if (!elements || !className) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach((el) => el?.classList.toggle(className, force));
    },

    /**
     * Check if element has class
     * @param {Element} element - Element to check
     * @param {string} className - Class name
     * @returns {boolean}
     */
    has(element, className) {
      return element?.classList.contains(className) || false;
    },

    /**
     * Replace one class with another
     * @param {Element|Element[]} elements - Single element or array of elements
     * @param {string} oldClass - Class to replace
     * @param {string} newClass - New class
     */
    replace(elements, oldClass, newClass) {
      if (!elements || !oldClass || !newClass) return;
      const items = Array.isArray(elements) ? elements : [elements];
      items.forEach((el) => el?.classList.replace(oldClass, newClass));
    },
  },

  /**
   * Viewport breakpoint helpers
   */
  Media: {
    isUp(name) {
      const px = BREAKPOINTS[name];
      return px ? window.matchMedia(`(min-width: ${px}px)`).matches : false;
    },

    isDown(name) {
      const px = BREAKPOINTS[name];
      return px ? window.matchMedia(`(max-width: ${px - 1}px)`).matches : false;
    },
  },

  /**
   * IntersectionObserver Utilities
   * Simplified methods for observing element visibility
   */
  Observer: {
    /**
     * Create an IntersectionObserver with common defaults
     * @param {Function} callback - Function to call when intersection changes
     * @param {Object} options - IntersectionObserver options
     * @returns {IntersectionObserver}
     */
    create(callback, options = {}) {
      const defaultOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0,
        ...options,
      };
      return new IntersectionObserver(callback, defaultOptions);
    },

    /**
     * Observe element once and disconnect after first intersection
     * @param {Element|string} element - Element or selector
     * @param {Function} onVisible - Callback when element becomes visible
     * @param {Object} options - Observer options
     * @returns {IntersectionObserver}
     */
    once(element, onVisible, options = {}) {
      const callback = (entries, observer) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            onVisible(entry.target, entry);
            observer.disconnect();
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (target) observer.observe(target);

      return observer;
    },

    /**
     * Observe element visibility changes (both enter and exit)
     * @param {Element|string} element - Element or selector
     * @param {Function} onEnter - Callback when element enters viewport
     * @param {Function} onExit - Callback when element exits viewport
     * @param {Object} options - Observer options
     * @returns {IntersectionObserver}
     */
    onChange(element, onEnter, onExit, options = {}) {
      const callback = (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && onEnter) {
            onEnter(entry.target, entry);
          } else if (!entry.isIntersecting && onExit) {
            onExit(entry.target, entry);
          }
        });
      };

      const observer = this.create(callback, options);
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (target) observer.observe(target);

      return observer;
    },
  },

  /**
   * URL & Query Parameter Utilities
   * Simplified methods for working with URLs and query parameters
   */
  URL: {
    /**
     * Get single query parameter value
     * @param {string} name - Parameter name
     * @param {string} url - URL to parse (default: current URL)
     * @returns {string|null}
     */
    getParam(name, url = window.location.search) {
      const params = new URLSearchParams(url);
      return params.get(name);
    },

    /**
     * Build query string from object or FormData
     * @param {Object|FormData} data - Object or FormData with key-value pairs
     * @returns {string} - Query string
     */
    buildQuery(data) {
      if (data instanceof FormData) {
        return new URLSearchParams(Array.from(data)).toString();
      }

      const params = new URLSearchParams();
      Object.entries(data).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.append(key, value);
        }
      });
      return params.toString();
    },

    /**
     * Update URL with query string (adds to browser history)
     * @param {string} queryString - Query string to set
     * @param {Object} options - Options {state, path}
     */
    updateUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.pushState(state, '', newUrl);
    },

    /**
     * Replace current URL without adding to browser history
     * @param {string} queryString - Query string to set
     * @param {Object} options - Options {state, path}
     */
    replaceUrl(queryString, options = {}) {
      const { state = {}, path = window.location.pathname } = options;
      const newUrl = queryString ? `${path}?${queryString}` : path;
      window.history.replaceState(state, '', newUrl);
    },

    /**
     * Remove hash from URL without adding to browser history
     * @param {Object} options - Options {state, keepQuery}
     */
    removeHash(options = {}) {
      const { state = {}, keepQuery = true } = options;
      const path = window.location.pathname;
      const search = keepQuery ? window.location.search : '';
      const newUrl = `${path}${search}`;
      window.history.replaceState(state, '', newUrl);
    },
  },

  /**
   * Animation & Transition Utilities
   * Simplified methods for common animation patterns
   */
  Animate: {
    /**
     * Internal fade effect implementation
     * @private
     * @param {Element} elem - Element to animate
     * @param {string} action - 'fadeIn' or 'fadeOut'
     */
    _fadeEffect(elem, action) {
      const FADE_STEP = 0.01;

      if (action === 'fadeIn') {
        elem.style.display = 'block';
        elem.style.opacity = 0;
        const fadeIn = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue < 1) {
            newValue += FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeIn);
          } else {
            elem.style.opacity = 1;
          }
        };
        requestAnimationFrame(fadeIn);
      } else if (action === 'fadeOut') {
        elem.style.opacity = 1;
        const fadeOut = () => {
          let newValue = parseFloat(elem.style.opacity);
          if (newValue > 0) {
            newValue -= FADE_STEP;
            elem.style.opacity = newValue;
            requestAnimationFrame(fadeOut);
          } else {
            elem.style.opacity = 0;
            elem.style.display = 'none';
          }
        };
        requestAnimationFrame(fadeOut);
      }
    },

    /**
     * Fade in element
     * @param {Element|string} element - Element or selector
     * @param {Function} callback - Callback when fade completes (optional)
     */
    fadeIn(element, callback) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      this._fadeEffect(target, 'fadeIn');
      if (callback) {
        callback();
      }
    },

    /**
     * Fade out element
     * @param {Element|string} element - Element or selector
     * @param {Function} callback - Callback when fade completes (optional)
     */
    fadeOut(element, callback) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      this._fadeEffect(target, 'fadeOut');
      if (callback) {
        callback();
      }
    },

    /**
     * Show message with fade in/out effect
     * @param {Element|string} element - Element or selector
     * @param {number} duration - How long to show message in ms (default: 3000)
     */
    showMessage(element, duration = 3000) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      this.fadeIn(target, () => {
        setTimeout(() => {
          this.fadeOut(target);
        }, duration);
      });
    },

    /**
     * Smooth class change using requestAnimationFrame
     * @param {Element|string} element - Element or selector
     * @param {Function} updateFn - Function to execute on next frame
     */
    smooth(element, updateFn) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      requestAnimationFrame(() => updateFn(target));
    },

    /**
     * Set loading state on element
     * @param {Element|string} element - Element or selector
     * @param {boolean} isLoading - Loading state
     */
    setLoading(element, isLoading) {
      const target = typeof element === 'string' ? Utility.DomSelector.$(element) : element;
      if (!target) return;

      if (isLoading) {
        target.setAttribute('aria-disabled', 'true');
        target.classList.add('loading');
      } else {
        target.classList.remove('loading');
        target.removeAttribute('aria-disabled');
      }
    },

    /**
     * Vibrate mobile device for haptic feedback
     * @param {number|Array<number>} pattern - Vibration duration in ms or pattern (default: 100)
     */
    vibrate(pattern = 100) {
      if (navigator.vibrate) {
        navigator.vibrate(pattern);
      }
    },
  },

  /**
   * Async form submit: native disabled, aria-busy, and theme spinner.
   * @param {HTMLButtonElement|null|undefined} button
   * @param {boolean} isLoading
   */
  setSubmitButtonLoading(button, isLoading) {
    if (!button) return;
    const on = !!isLoading;
    button.disabled = on;
    if (on) {
      button.setAttribute('aria-busy', 'true');
    } else {
      button.removeAttribute('aria-busy');
    }
    Utility.Animate.setLoading(button, on);
  },

  /**
   * Focus Management Utilities
   * ADA-compliant focus management for modals, drawers, and interactive elements
   */
  Focus: {
    _handlers: {},
    _activeContainer: null,
    _previouslyFocused: null,
    _forceTimeout: null,

    /**
     * Check if element is visible and focusable
     * @private
     */
    _isVisible(element) {
      const style = window.getComputedStyle(element);
      return style.display !== 'none' && style.visibility !== 'hidden' && style.opacity !== '0';
    },

    /**
     * Handle arrow key navigation between focusable elements
     * @param {KeyboardEvent} event - Keyboard event
     * @param {Element} container - Container to navigate within
     * @param {boolean} includeHidden - Include hidden elements
     * @returns {boolean} True if navigation was handled
     */
    handleArrowKeys(event, container, includeHidden = false) {
      if (!container) return false;

      const arrowKeys = ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'];
      if (!arrowKeys.includes(event.key)) return false;
      if (!container.contains(document.activeElement)) return false;

      const focusableElements = this.getFocusableElements(container, includeHidden);
      if (!focusableElements.length) return false;

      event.preventDefault();

      const currentIndex = focusableElements.findIndex(
        (el) => el === document.activeElement || el.contains(document.activeElement)
      );
      let nextIndex = currentIndex;

      if (currentIndex === -1) {
        nextIndex = 0;
      } else if (event.key === 'ArrowDown' || event.key === 'ArrowRight') {
        nextIndex = currentIndex < focusableElements.length - 1 ? currentIndex + 1 : 0;
      } else if (event.key === 'ArrowUp' || event.key === 'ArrowLeft') {
        nextIndex = currentIndex > 0 ? currentIndex - 1 : focusableElements.length - 1;
      }

      if (nextIndex >= 0 && nextIndex < focusableElements.length) {
        focusableElements[nextIndex].focus();
        return true;
      }

      return false;
    },

    /**
     * Get all focusable elements within a container
     * @param {Element} container
     * @param {boolean} includeHidden - Include hidden elements
     * @returns {Array<Element>}
     */
    getFocusableElements(container, includeHidden = false) {
      if (!container) {
        console.warn('Focus.getFocusableElements: No container provided');
        return [];
      }

      const selector = [
        '.filter__container',
        'a[href]',
        'a[data-href]',
        '[type="button"]',
        'button:enabled',
        'input:not([type=hidden]):not([tabindex^="-"]):enabled',
        'select:enabled',
        'textarea:enabled',
        'summary',
        '[contenteditable]:not([contenteditable="false"])',
        'details',
        'audio[controls]',
        'video[controls]',
        'embed',
        'object',
        'iframe',
        'area[href]',
        '[tabindex]:not([tabindex^="-"])',
        '[draggable="true"]',
      ].join(', ');

      const elements = Array.from(container.querySelectorAll(selector));

      return elements.filter((el) => {
        if (el.getAttribute('aria-hidden') === 'true' || el.hasAttribute('inert')) return false;
        return includeHidden || this._isVisible(el);
      });
    },

    /**
     * Force focus on an element
     * @param {Element} element
     * @param {object} options - {preventScroll, timeout}
     * @returns {boolean} Success status
     */
    force(element, options = {}) {
      const { preventScroll = false, timeout = 500 } = options;

      if (!element || !this._isVisible(element)) {
        console.warn('Focus.force: Invalid or invisible element', element);
        return false;
      }

      if (this._forceTimeout) clearTimeout(this._forceTimeout);

      element.focus({ preventScroll });
      if (element === document.activeElement) return true;

      const hadTabindex = element.hasAttribute('tabindex');
      const originalTabindex = element.getAttribute('tabindex');
      element.setAttribute('tabindex', '0');

      this._forceTimeout = setTimeout(() => {
        element.focus({ preventScroll });
        if (!hadTabindex) {
          element.removeAttribute('tabindex');
        } else if (originalTabindex !== '0') {
          element.setAttribute('tabindex', originalTabindex);
        }
        if (element !== document.activeElement) {
          console.warn('Focus.force: Unable to focus after retry');
        }
      }, timeout);

      return element === document.activeElement;
    },

    trap(container, elementToFocus = null, options = {}) {
      const {
        returnFocus = true,
        skipVisibilityCheck = false,
        includeHidden = false,
        enableArrowKeys = false,
      } = options;

      if (!container) return false;
      if (!skipVisibilityCheck && !this._isVisible(container)) {
        requestAnimationFrame(() =>
          setTimeout(
            () =>
              this.trap(container, elementToFocus, {
                ...options,
                skipVisibilityCheck: true,
                includeHidden: true,
              }),
            50
          )
        );
        return false;
      }

      const checkHidden = includeHidden || skipVisibilityCheck;
      const elements = this.getFocusableElements(container, checkHidden);
      if (!elements.length) {
        container.setAttribute('tabindex', '-1');
        return false;
      }

      if (returnFocus && document.activeElement !== document.body) {
        this._previouslyFocused = document.activeElement;
      }
      this.release(null, false);
      this._activeContainer = container;

      this._handlers.keydown = (e) => {
        if (e.key === 'Tab' || e.keyCode === 9) {
          const current = this.getFocusableElements(container, checkHidden);
          if (!current.length || !container.contains(document.activeElement)) return;
          const [first, last] = [current[0], current[current.length - 1]];
          if (
            (document.activeElement === last && !e.shiftKey) ||
            (document.activeElement === first && e.shiftKey)
          ) {
            e.preventDefault();
            (document.activeElement === last ? first : last).focus();
          }
          return;
        }

        if (enableArrowKeys) {
          this.handleArrowKeys(e, container, checkHidden);
        }
      };

      this._handlers.focusin = (e) => {
        if (this._activeContainer && !this._activeContainer.contains(e.target)) {
          e.preventDefault();
          e.stopPropagation();
          this.getFocusableElements(container, checkHidden)[0]?.focus();
        }
      };

      document.addEventListener('keydown', this._handlers.keydown, true);
      document.addEventListener('focusin', this._handlers.focusin, true);
      requestAnimationFrame(() => this.force(elementToFocus || elements[0]));
      return true;
    },

    /**
     * Release focus trap
     * @param {Element} elementToFocus - Element to focus after release
     * @param {boolean} usePrevious - Use stored previous focus
     */
    release(elementToFocus = null, usePrevious = true) {
      ['focusin', 'keydown'].forEach((type) => {
        if (this._handlers[type]) {
          document.removeEventListener(type, this._handlers[type], true);
        }
      });

      this._handlers = {};
      this._activeContainer = null;

      if (this._forceTimeout) {
        clearTimeout(this._forceTimeout);
        this._forceTimeout = null;
      }

      const returnElement = elementToFocus || (usePrevious ? this._previouslyFocused : null);

      if (returnElement?.isConnected) {
        try {
          returnElement.focus();
        } catch (error) {
          console.warn('Focus.release: Unable to return focus', error);
        }
      }

      this._previouslyFocused = null;
    },

    /** Check if focus is trapped */
    isTrapped() {
      return this._activeContainer !== null;
    },

    /** Get active trapped container */
    getActiveContainer() {
      return this._activeContainer;
    },

    /**
     * Store element identifier for focus restoration after DOM update
     * @param {Element} element - Element to store (defaults to activeElement)
     * @param {Element} container - Optional container to check if element is within
     * @returns {Object|null} Stored element identifier or null
     */
    storeForRestore(element = null, container = null) {
      const activeElement = element || document.activeElement;
      if (!activeElement) return null;

      if (container && !container.contains(activeElement)) return null;

      if (activeElement.id) {
        return { selector: `#${activeElement.id}` };
      }
      if (activeElement.getAttribute('for')) {
        return { selector: `label[for="${activeElement.getAttribute('for')}"]` };
      }
      if (activeElement.href) {
        return { selector: `a[href="${activeElement.href}"]` };
      }
      return activeElement.isConnected ? { element: activeElement } : null;
    },

    /**
     * Restore focus to a previously stored element
     * @param {Object} storedElement - Element identifier from storeForRestore
     * @param {Element} context - Optional context element to search within
     * @returns {boolean} True if focus was restored
     */
    restore(storedElement, context = document) {
      if (!storedElement) return false;

      let elementToFocus = null;

      if (storedElement.selector) {
        elementToFocus = Utility.DomSelector.$(storedElement.selector, context);
      } else if (storedElement?.element?.isConnected) {
        elementToFocus = storedElement.element;
      }

      if (elementToFocus?.isConnected) {
        requestAnimationFrame(() => {
          elementToFocus.focus();
        });
        return true;
      }

      return false;
    },
  },

  /**
   * Fetch utilities with error handling and common patterns
   */
  Fetch: {
    /**
     * Fetch with automatic error handling
     * @param {string} url - Request URL
     * @param {object} options - Fetch options
     * @returns {Promise<Response>}
     */
    async request(url, options = {}) {
      try {
        const response = await fetch(url, options);
        if (!response.ok) {
          const error = new Error(`HTTP error! status: ${response.status}`);
          error.status = response.status;
          error.response = response;

          try {
            const contentType = response.headers.get('content-type') || '';
            const isJson = contentType.includes('application/json');
            const data = isJson ? await response.json() : await response.text();
            error.data = data;

            if (isJson) {
              const message = data?.message?.trim() || data?.description?.trim();
              if (message) error.message = message;
            } else if (data?.trim()) {
              error.message = data.trim();
            }
          } catch (parseError) {
            error.parseError = parseError;
          }

          throw error;
        }
        return response;
      } catch (error) {
        console.error('Fetch error:', error);
        const fallback = error?.message || 'Request Failed';
        Utility.handleError(fallback, 'Request Failed');
        throw error;
      }
    },

    /**
     * Fetch JSON data
     * @param {string} url
     * @param {object} options
     * @returns {Promise<object>}
     */
    async json(url, options = {}) {
      const response = await this.request(url, options);
      return response.json();
    },

    /**
     * Fetch HTML text
     * @param {string} url
     * @param {object} options
     * @returns {Promise<string>}
     */
    async html(url, options = {}) {
      const response = await this.request(url, options);
      return response.text();
    },

    /**
     * Fetch Shopify section HTML
     * @param {string} url - Base URL (can include existing query parameters)
     * @param {string|array} sectionIds - Section ID(s)
     * @returns {Promise<object>}
     */
    async section(url, sectionIds) {
      const sections = Array.isArray(sectionIds) ? sectionIds.join(',') : sectionIds;
      const separator = url.includes('?') ? '&' : '?';
      const sectionUrl = `${url}${separator}sections=${sections}`;
      return this.json(sectionUrl);
    },

    /**
     * Fetch product data by handle
     * @param {string} handle - Product handle
     * @param {string} view - View template (optional)
     * @returns {Promise<string>}
     */
    async product(handle, view = null) {
      const url = view ? `/products/${handle}?view=${view}` : `/products/${handle}`;
      return this.html(url);
    },

    /**
     * Build fetch config for POST requests
     * @param {string} type - request Accept type ('json' or 'javascript')
     * @param {object} config - Additional config (headers, body)
     * @returns {object} Fetch configuration object
     */
    config(type = 'json', config = {}) {
      const headers = {
        'Content-Type': 'application/json',
        Accept: `application/${type}`,
        ...config.headers,
      };

      if (type === 'javascript') {
        headers['X-Requested-With'] = 'XMLHttpRequest';
        delete headers['Content-Type'];
      }

      return {
        method: 'POST',
        headers,
        body: config.body,
      };
    },
  },

  /**
   * Storage Management Utilities
   * Unified methods for cookies, localStorage, and sessionStorage
   */
  Storage: {
    Cookie: {
      set(name, value, options = {}) {
        const { days = 365, path = '/', domain = '', secure = false, sameSite = 'Lax' } = options;
        const val = typeof value === 'object' ? JSON.stringify(value) : String(value);
        const date = new Date(Date.now() + days * 864e5);
        let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(val)}; expires=${date.toUTCString()}; path=${path}`;
        if (domain) cookie += `; domain=${domain}`;
        if (secure) cookie += '; secure';
        if (sameSite) cookie += `; samesite=${sameSite}`;
        document.cookie = cookie;
        return true;
      },

      get(name, parseJSON = false) {
        const nameEQ = `${encodeURIComponent(name)}=`;
        const cookie = document.cookie.split(';').find((c) => c.trim().indexOf(nameEQ) === 0);
        if (!cookie) return null;
        const value = decodeURIComponent(cookie.trim().substring(nameEQ.length));
        return parseJSON
          ? (() => {
              try {
                return JSON.parse(value);
              } catch {
                return value;
              }
            })()
          : value;
      },

      remove(name, options = {}) {
        return this.set(name, '', { ...options, days: -1 });
      },

      exists(name) {
        return this.get(name) !== null;
      },

      getAll() {
        const cookies = {};
        document.cookie.split(';').forEach((c) => {
          const [n, v] = c.trim().split('=');
          if (n) cookies[decodeURIComponent(n)] = decodeURIComponent(v || '');
        });
        return cookies;
      },
    },

    Local: {
      set(key, value, options = {}) {
        try {
          const data = {
            value,
            timestamp: Date.now(),
            ...(options.ttl && { expiry: Date.now() + options.ttl }),
          };
          localStorage.setItem(key, JSON.stringify(data));
          return true;
        } catch {
          return false;
        }
      },

      get(key, defaultValue = null) {
        try {
          const item = localStorage.getItem(key);
          if (!item) return defaultValue;
          const data = JSON.parse(item);
          if (data.expiry && Date.now() > data.expiry) {
            this.remove(key);
            return defaultValue;
          }
          return data.value;
        } catch {
          return localStorage.getItem(key) || defaultValue;
        }
      },

      remove(key) {
        try {
          localStorage.removeItem(key);
          return true;
        } catch {
          return false;
        }
      },
      clear() {
        try {
          localStorage.clear();
          return true;
        } catch {
          return false;
        }
      },
      exists(key) {
        return localStorage.getItem(key) !== null;
      },
      keys() {
        return Object.keys(localStorage);
      },
      size() {
        return Object.keys(localStorage).reduce(
          (s, k) => s + k.length + localStorage[k].length,
          0
        );
      },
    },

    Session: {
      set(key, value) {
        try {
          sessionStorage.setItem(key, JSON.stringify({ value, timestamp: Date.now() }));
          return true;
        } catch {
          return false;
        }
      },

      get(key, defaultValue = null) {
        try {
          const item = sessionStorage.getItem(key);
          return item ? JSON.parse(item).value : defaultValue;
        } catch {
          return sessionStorage.getItem(key) || defaultValue;
        }
      },

      remove(key) {
        try {
          sessionStorage.removeItem(key);
          return true;
        } catch {
          return false;
        }
      },
      clear() {
        try {
          sessionStorage.clear();
          return true;
        } catch {
          return false;
        }
      },
      exists(key) {
        return sessionStorage.getItem(key) !== null;
      },
      keys() {
        return Object.keys(sessionStorage);
      },
    },
  },

  /**
   * DOM Utilities
   * Methods for parsing and manipulating HTML
   */
  DOM: {
    /**
     * Parse HTML string to DOM
     * @param {string} htmlString - HTML string to parse
     * @returns {Document}
     */
    parse(htmlString) {
      return new DOMParser().parseFromString(htmlString, 'text/html');
    },

    /**
     * Update destination element from source HTML
     * @param {string} id - Selector for element to update
     * @param {Document} html - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    update(id, html, container = document) {
      const source = html.querySelector(id);
      const destination = container.querySelector(id);

      if (source && destination) {
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      } else {
        destination?.classList.add('d-none');
      }
    },

    /**
     * Update multiple different selectors from parsed HTML
     * @param {Array<string>} selectors - Array of selectors to update
     * @param {Document} sourceHTML - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    updateSelectors(selectors, sourceHTML, container = document) {
      selectors.forEach((selector) => this.update(selector, sourceHTML, container));
    },

    /**
     * Keeps Points/Bonus line-property holders in sync after variant section HTML swap.
     * @param {ParentNode} container
     * @param {ParentNode} html
     */
    syncConsultantVariantLineProperties(container = document, html = null) {
      if (!container?.querySelector || !html?.querySelector) return;

      [
        { selector: '[data-variant-consultant-points]', inputName: 'properties[Points]', dataKey: 'points' },
        { selector: '[data-variant-consultant-bonus]', inputName: 'properties[Bonus]', dataKey: 'bonus' },
      ].forEach(({ selector, inputName, dataKey }) => {
        const source = html.querySelector(selector);
        const destination = container.querySelector(selector);
        if (!source || !destination) return;

        const sourceInput = source.querySelector(`input[name="${inputName}"]`);
        const destinationInput = destination.querySelector(`input[name="${inputName}"]`);
        if (sourceInput && destinationInput) {
          destinationInput.value = sourceInput.value;
        }

        const value = String(sourceInput?.value || source.dataset[dataKey] || '').trim();

        if (value) {
          destination.dataset[dataKey] = value;
        } else {
          delete destination.dataset[dataKey];
        }
      });
    },

    /**
     * Render HTML stored in variant product_details text metafields.
     * @param {ParentNode} [container]
     */
    hydrateVariantProductDetailsHtml(container = document) {
      const scope = container === document ? document : container;
      if (!scope?.querySelectorAll) return;

      scope.querySelectorAll('[data-variant-product-details]').forEach((root) => {
        const source = root.querySelector('script.variant-product-details-source');
        if (source) {
          try {
            const html = JSON.parse(source.textContent);
            if (typeof html === 'string' && html.trim()) {
              root.innerHTML = html;
              return;
            }
          } catch {
            /* fall through */
          }
        }

        const field = root.querySelector(
          '.metafield-multi_line_text_field, .metafield-single_line_text_field'
        );
        if (!field) return;

        const text = (field.textContent || '').trim();
        if (/^<[a-z][\s\S]*>/i.test(text)) {
          root.innerHTML = text;
        }
      });
    },

    /**
     * Hide product page collapsible rows when toggle-content-wrap has no meaningful content.
     * @param {ParentNode} [container]
     * @param {ParentNode} [html] - Optional parsed section HTML from variant change
     */
    syncProductCollapsibleRows(container = document, html = null) {
      const scope = container === document ? document : container;
      if (!scope?.querySelectorAll) return;

      scope.querySelectorAll('[data-product-collapsible-row]').forEach((destPanel) => {
        const blockId = destPanel.dataset.collapsibleRowId;
        const sourcePanel =
          html && blockId
            ? html.querySelector(`[data-collapsible-row-id="${CSS.escape(blockId)}"]`)
            : null;
        const destWrap = destPanel.querySelector('.toggle-content-wrap');

        let isEmpty = true;
        if (destWrap) {
          const hasMedia = Boolean(
            destWrap.querySelector('img, video, iframe, svg, picture, audio')
          );
          const text = (destWrap.textContent || '').replace(/\s+/g, ' ').trim();
          isEmpty = !hasMedia && text.length === 0;
        }

        if (sourcePanel) {
          isEmpty = sourcePanel.classList.contains('d-none') || isEmpty;
        }

        destPanel.classList.toggle('d-none', isEmpty);
      });
    },

    /**
     * Update all matching elements from parsed HTML
     * @param {string} selector - CSS selector (may match multiple elements)
     * @param {Document} html - Parsed HTML document
     * @param {Element} container - Target container (default: document)
     */
    updateAll(selector, html, container = document) {
      const sources = html.querySelectorAll(selector);
      const destinations = container.querySelectorAll(selector);

      if (destinations.length === 0) {
        return;
      }
      if (sources.length === 0) {
        destinations.forEach((dest) => dest.classList.add('d-none'));
        return;
      }

      destinations.forEach((destination, index) => {
        const source = sources[index] || sources[0];
        destination.innerHTML = source.innerHTML;
        destination.classList.remove('d-none');
      });
    },

    /**
     * Extract element from HTML string
     * @param {string|Document} htmlString - HTML string or parsed document
     * @param {string} selector - CSS selector
     * @returns {Element|null}
     */
    extract(htmlString, selector) {
      const parsed = typeof htmlString === 'string' ? this.parse(htmlString) : htmlString;
      return parsed.querySelector(selector);
    },

    /**
     * Read visible or aria-label text from an element
     * @param {Element} element
     * @returns {string}
     */
    readElementText(element) {
      if (!element) return '';
      const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;
      const ariaLabel = element.getAttribute('aria-label');
      return ariaLabel ? ariaLabel.trim() : '';
    },

    /**
     * Read Rebuy product title text (strips View / Quick view from aria-label)
     * @param {Element} element
     * @returns {string}
     */
    readRebuyTitleText(element) {
      if (!element) return '';

      const text = (element.textContent || '').replace(/\s+/g, ' ').trim();
      if (text) return text;

      const ariaLabel = element.getAttribute('aria-label');
      if (ariaLabel) {
        return ariaLabel
          .replace(/^View\s+/i, '')
          .replace(/^Quick view\s+/i, '')
          .trim();
      }

      return '';
    },
  },

  /**
   * Form Utilities
   * Methods for form handling and validation
   */
  Form: {
    /**
     * Serialize form data
     * @param {HTMLFormElement} form - Target form element
     * @returns {Object} Serialized form data
     */
    serialize(form) {
      if (!(form instanceof HTMLFormElement)) {
        throw new TypeError('Expected a form element');
      }

      const obj = {};
      const formData = new FormData(form);
      const regex = /^properties\[(.*?)\]$/;

      for (const key of formData.keys()) {
        const match = regex.exec(key);
        if (match) {
          obj.properties = obj.properties || {};
          obj.properties[match[1]] = formData.get(key);
        } else {
          obj[key] = formData.get(key);
        }
      }
      return obj;
    },

    /**
     * Validate email format
     * @param {string} email - Email to validate
     * @returns {boolean} Whether email is valid
     */
    validateEmail(email) {
      return !!email.match(
        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/
      );
    },

    /**
     * Validate form and set ARIA attributes
     * @param {HTMLFormElement} form - Form to validate
     * @returns {Array<string>} Array of error messages
     */
    validate(form) {
      const errors = [];
      const requiredFields = form.querySelectorAll('[required]');

      requiredFields.forEach((field) => {
        if (!field.value.trim()) {
          field.setAttribute('aria-invalid', 'true');
          field.setAttribute('aria-describedby', `error-${field.id}`);
          errors.push(`${field.getAttribute('aria-label') || field.name} is required`);
        } else {
          field.removeAttribute('aria-invalid');
        }
      });

      return errors;
    },
  },

  /**
   * Cart Management Utilities
   */
  Cart: {
    _cache: null,
    _cacheFetchedAt: 0,
    _fetchPromise: null,
    FETCH_MIN_MS: 2000,

    _getAddUrl() {
      return globalThis.routes?.cart_add_url || '/cart/add.js';
    },

    _getCartUrl() {
      return globalThis.routes?.cart_url ? `${globalThis.routes.cart_url}.js` : '/cart.js';
    },

    /**
     * Fetch current cart
     * @param {boolean} force - Skip cache
     * @returns {Promise<object>}
     */
    async fetch(force = false) {
      const now = Date.now();

      if (this._fetchPromise) return this._fetchPromise;
      if (!force && this._cache) return this._cache;
      if (force && this._cache && now - this._cacheFetchedAt < this.FETCH_MIN_MS) {
        return this._cache;
      }

      this._fetchPromise = Utility.Fetch.json(this._getCartUrl(), {
        headers: { Accept: 'application/json' },
      })
        .then((data) => {
          this._cache = data;
          this._cacheFetchedAt = Date.now();
          return data;
        })
        .finally(() => {
          this._fetchPromise = null;
        });

      return this._fetchPromise;
    },

    clearCache() {
      this._cache = null;
      this._cacheFetchedAt = 0;
    },

    isSampleItem(item) {
      const props = item?.properties;
      if (props) {
        if (Array.isArray(props)) {
          const match = props.find((entry) => entry && (entry.name === '_sample' || entry.key === '_sample'));
          if (match && (match.value === true || match.value === 'true')) return true;
        } else if (typeof props === 'object' && ('_sample' in props)) {
          const sample = props._sample;
          if (sample === true || sample === 'true') return true;
        }
      }
      const tags = item?.product?.tags ?? item?.tags ?? item?.product_tags ?? [];
      const tagList = Array.isArray(tags)
        ? tags.map((t) => String(t).toLowerCase())
        : String(tags)
            .split(',')
            .map((t) => t.trim().toLowerCase())
            .filter(Boolean);
      return tagList.includes('sample');
    },

    isGiftItem(item) {
      const props = item?.properties || {};
      const gift = props._gift;
      const giftOption = props._gift_option;
      return gift === true || gift === 'true' || giftOption === true || giftOption === 'true';
    },

    /**
     * Update cart count in header
     * @param {object} [cart] - Optional cart object with item_count
     */
    updateHeaderCount(cart) {
      const itemCount =
        cart?.item_count ??
        globalThis.themeData?.cart?.item_count ??
        this._cache?.item_count ??
        0;

      const cartIcons = document.querySelectorAll('[data-cart-icon]');
      cartIcons.forEach((icon) => {
        const cartCount = icon.querySelector('[data-cart-count]');
        if (cartCount) {
          cartCount.innerHTML = itemCount;
          cartCount.classList.toggle('d-none', itemCount === 0);
        }
      });
    },

    /**
     * Add one or more items using Shopify's multi-item add format.
     * Uses `{ items: [...] }` so theme interceptors (e.g. Klaviyo in bundle.js) receive `items[0]`.
     * @param {Array<object>|object} items - Line items or payload with `items` key
     * @returns {Promise<object>}
     */
    async addItems(items) {
      const payload = Array.isArray(items) ? { items } : items;

      // Prime bundle.js cart cache (`Ct`) before add.js handler runs.
      await this.fetch(true);

      const config = Utility.Fetch.config('json', {
        body: JSON.stringify(payload),
      });

      const response = await fetch(this._getAddUrl(), config);
      const data = await response.json();

      if (!response.ok || data.status === 422 || data.description) {
        throw new Error(data.description || data.message || 'Unable to add to cart');
      }

      this.clearCache();
      await this.fetch(true);
      return data;
    },

    /**
     * Add item to cart
     * @param {object} formData - Form data or item object
     * @returns {Promise<object>}
     */
    async addItem(formData) {
      if (formData?.items) {
        return this.addItems(formData);
      }

      return this.addItems([formData]);
    },
  },

  /**
   * Read JSON from <script type="application/json"> tags
   */
  JSONScript: {
    parse(id, fallback = null) {
      const el = Utility.DomSelector.byId(id);
      if (!el) return fallback;
      try {
        return JSON.parse(el.textContent || '');
      } catch (error) {
        console.warn(`JSONScript.parse: invalid JSON in #${id}`, error);
        return fallback;
      }
    },
  },

  /**
   * Alpine.js helpers
   */
  Alpine: {
    getStore(name) {
      if (!window.Alpine || typeof window.Alpine.store !== 'function') return null;
      try {
        return window.Alpine.store(name);
      } catch {
        return null;
      }
    },

    getCartStore() {
      return this.getStore('cart');
    },

    /**
     * Sync Alpine cart store after Rebuy add-to-cart
     * @param {object} [options]
     * @param {number} [options.timeout=800]
     * @returns {Promise<object|null>}
     */
    async syncCartFromRebuy(options = {}) {
      const cart = this.getCartStore();
      if (!cart) return null;

      const rebuyCart = window.Rebuy?.Cart;
      const skipRebuyFetch = options.skipRebuyFetch === true;
      const lightweight = options.lightweight === true;

      if (!skipRebuyFetch && rebuyCart && typeof rebuyCart.fetchShopifyCart === 'function') {
        const now = Date.now();
        if (!this._lastRebuyFetchAt || now - this._lastRebuyFetchAt > 2500) {
          rebuyCart.fetchShopifyCart();
          this._lastRebuyFetchAt = now;
        }
      }

      if (lightweight) {
        cart.updating = false;
        return cart;
      }

      const timeout = options.timeout || 800;

      await Promise.race([
        new Promise((resolve) => {
          const startedAt = Date.now();
          const check = () => {
            if (!cart.updating || Date.now() - startedAt > timeout) {
              resolve();
              return;
            }
            setTimeout(check, 50);
          };
          check();
        }),
        new Promise((resolve) => setTimeout(resolve, timeout)),
      ]);

      if (typeof cart.getState === 'function') {
        const data = await Promise.race([
          cart.getState(),
          new Promise((resolve) => setTimeout(() => resolve(null), 1500)),
        ]);

        if (data) {
          cart.data = data;
          cart.items = JSON.parse(JSON.stringify(data.items || []));
        }
      }

      cart.updating = false;

      if (typeof cart.load === 'function') {
        cart.load();
      }

      return cart;
    },
  },

  /**
   * Rebuy helpers
   */
  Rebuy: {
    _showTimer: null,
    _patchAttempts: 0,
    _maxPatchAttempts: 80,

    showSmartCart() {
      if (!window.Rebuy) return false;

      this.closeThemeCart();

      let shown = false;
      if (typeof window.Rebuy.SmartCart?.show === 'function') {
        window.Rebuy.SmartCart.show();
        shown = true;
      } else if (typeof window.Rebuy.smartCart?.show === 'function') {
        window.Rebuy.smartCart.show();
        shown = true;
      }

      if (shown) {
        this._pendingOpen = false;
        document.dispatchEvent(new CustomEvent('ellis:samples.mount'));
        Utility.Samples?.scheduleRebuyMount?.({ aggressive: true });
      }

      return shown;
    },

    hideSmartCart() {
      if (!window.Rebuy) return false;

      let hidden = false;
      if (typeof window.Rebuy.SmartCart?.hide === 'function') {
        window.Rebuy.SmartCart.hide();
        hidden = true;
      } else if (typeof window.Rebuy.smartCart?.hide === 'function') {
        window.Rebuy.smartCart.hide();
        hidden = true;
      }

      this._pendingOpen = false;
      return hidden;
    },

    isSmartCartVisible() {
      if (window.Rebuy?.SmartCart?.visible || window.Rebuy?.smartCart?.visible) {
        return true;
      }

      return !!Utility.DomSelector.$(
        '#rebuy-cart.is-visible, #rebuy-smart-cart.is-visible, .rebuy-cart.is-visible, .rebuy-smart-cart.is-visible'
      );
    },

    closeThemeCart() {
      const cart = Utility.Alpine.getCartStore();
      if (!cart) return;

      cart.isOpen = false;
      if (typeof cart.close === 'function') {
        try {
          cart.close();
        } catch {
          /* ignore */
        }
      }
    },

    scheduleShowSmartCart(delay = 120) {
      clearTimeout(this._showTimer);
      this._showTimer = setTimeout(() => this.showSmartCart(), delay);
    },

    scheduleShowSmartCartWithRetry() {
      this._pendingOpen = true;
      [0, 100, 300, 600, 1200, 2000, 3500, 5000].forEach((delay) => {
        setTimeout(() => {
          if (!this._pendingOpen) return;
          if (window.Rebuy?.SmartCart?.visible || this.isSmartCartVisible()) {
            this._pendingOpen = false;
            return;
          }
          this.showSmartCart();
        }, delay);
      });
    },

    findCartRoot() {
      const selectors = [
        '#rebuy-cart',
        '#rebuy-smart-cart',
        '.rebuy-cart.is-visible',
        '.rebuy-cart',
        '[data-rebuy-component="smart-cart"]',
        '.rebuy-smart-cart',
        'rebuy-smart-cart',
        'rebuy-cart',
      ];
      for (let i = 0; i < selectors.length; i += 1) {
        const el = Utility.DomSelector.$(selectors[i]);
        if (el) return el;
      }
      return null;
    },

    exposeGlobals() {
      window.__ellisShowSmartCart = () => this.showSmartCart();
      window.__ellisScheduleShowRebuySmartCart = () => this.scheduleShowSmartCartWithRetry();
      this.bindCartAddPatchEvents();
      this.ensureCartAddPatch();

      if (window.__ellisSmartCartReadyRetry) return;
      window.__ellisSmartCartReadyRetry = true;
      ['rebuy:smartcart.ready', 'rebuy:cart.ready'].forEach((eventName) => {
        document.addEventListener(eventName, () => {
          if (!this._pendingOpen) return;
          this.showSmartCart();
        });
      });

      this.openSmartCartFromUrlParam();
    },

    openSmartCartFromUrlParam() {
      const cartParam = Utility.URL.getParam('cart');
      const smartCartParam = Utility.URL.getParam('smart_cart');
      if (cartParam !== 'true' && smartCartParam !== '1') return;

      this._pendingOpen = true;
      this.scheduleShowSmartCartWithRetry();
    },

    getWidget(root) {
      if (!root || !window.Rebuy) return null;
      if (root.__rebuyWidget) return root.__rebuyWidget;
      const widgetId = root.getAttribute('data-rebuy-id');
      const widgets = window.Rebuy.widgets || window.Rebuy.Widgets || [];
      for (let i = 0; i < widgets.length; i += 1) {
        if (String(widgets[i].id) === widgetId) return widgets[i];
      }
      return null;
    },

    setWidgetRef(root, widget) {
      if (root && widget) {
        root.__rebuyWidget = widget;
      }
    },

    getWidgetRoot(root, block) {
      if (root?.hasAttribute?.('data-rebuy-id')) return root;
      if (block) {
        const widgetRoot = block.closest('[data-rebuy-id]');
        if (widgetRoot) return widgetRoot;
      }
      return root || null;
    },

    getBlockProductId(block) {
      const match = block?.className?.match(/product-id-(\d+)/);
      return match ? match[1] : null;
    },

    getBlockProduct(root, block) {
      const widget = this.getWidget(root);
      const products = widget?.data?.products;
      if (!products?.length || !block) return null;

      const productId = this.getBlockProductId(block);
      if (productId) {
        for (let i = 0; i < products.length; i += 1) {
          if (String(products[i].id) === productId) return products[i];
        }
      }

      const blocks = root?.querySelectorAll('.rebuy-product-block');
      if (!blocks?.length) return null;
      const index = Array.prototype.indexOf.call(blocks, block);
      return index >= 0 ? products[index] || null : null;
    },

    getSelectedVariantId(block, root) {
      if (!block) return 0;

      const select = block.querySelector(
        '.rebuy-product-options select, select[name="id"], select.rebuy-select'
      );
      if (select?.value) return Number(select.value);

      const widgetRoot = this.getWidgetRoot(root, block);
      const product = this.getBlockProduct(widgetRoot, block);
      if (product?.selected_variant?.id) return Number(product.selected_variant.id);

      const variants = product?.variants;
      if (Array.isArray(variants) && variants.length) {
        const selected =
          variants.find((variant) => variant?.selected) ||
          variants.find((variant) => variant?.available !== false) ||
          variants[0];
        if (selected?.id) return Number(selected.id);
      }

      return 0;
    },

    normalizeAddPayload(data) {
      if (!data || typeof data !== 'object') return null;

      const id = Number(data.id ?? data.variant_id ?? data.variantId);
      if (!id) return null;

      const item = {
        id,
        quantity: Number(data.quantity || 1),
      };

      if (data.properties) item.properties = data.properties;
      if (data.selling_plan) item.selling_plan = data.selling_plan;

      return item;
    },

    /**
     * Add via Shopify `{ items: [...] }` so Klaviyo/bundle.js interceptors receive `items[0]`.
     * @param {object|Array<object>} items
     * @param {object} [options]
     * @param {boolean} [options.showSmartCart=false]
     * @returns {Promise<object>}
     */
    async addViaShopify(items, options = {}) {
      const list = Array.isArray(items) ? items : [items];
      const payload = list
        .map((item) => this.normalizeAddPayload(item))
        .filter(Boolean);

      if (!payload.length) throw new Error('Missing variant id');

      const data = await Utility.Cart.addItems(payload);

      const rebuyCart = window.Rebuy?.Cart;
      if (rebuyCart && typeof rebuyCart.fetchShopifyCart === 'function') {
        await new Promise((resolve) => {
          rebuyCart.fetchShopifyCart(() => resolve());
        });
        Utility.Samples?.hydrateCacheFromRebuy?.();
      }

      await Utility.Alpine.syncCartFromRebuy({ skipRebuyFetch: true, lightweight: true });

      if (options.showSmartCart) {
        this.scheduleShowSmartCartWithRetry();
      }

      Utility.Samples?.scheduleProgressRefresh?.(300);

      return data;
    },

    patchCartAddItem() {
      const rebuyCart = window.Rebuy?.Cart;
      if (!rebuyCart || typeof rebuyCart.addItem !== 'function' || rebuyCart.__ellisAddPatch) {
        return false;
      }

      const originalAddItem = rebuyCart.addItem.bind(rebuyCart);

      rebuyCart.addItem = function ellisPatchedAddItem(data, callbacks) {
        const list = Array.isArray(data) ? data : [data];
        const items = list.map((entry) => Utility.Rebuy.normalizeAddPayload(entry)).filter(Boolean);

        if (!items.length) {
          const error = new Error('Missing variant id');
          if (callbacks && typeof callbacks.error === 'function') {
            callbacks.error(error);
          }
          return Promise.reject(error);
        }

        return Utility.Rebuy.addViaShopify(items)
          .then((result) => {
            if (typeof callbacks === 'function') {
              callbacks(result);
            } else if (callbacks && typeof callbacks.success === 'function') {
              callbacks.success(result);
            }
            return result;
          })
          .catch((error) => {
            if (callbacks && typeof callbacks.error === 'function') {
              callbacks.error(error);
            }
            throw error;
          });
      };

      rebuyCart.__ellisAddPatch = true;
      rebuyCart.__ellisOriginalAddItem = originalAddItem;
      return true;
    },

    ensureCartAddPatch() {
      if (this.patchCartAddItem()) {
        this._patchAttempts = 0;
        return;
      }

      if (this._patchAttempts >= this._maxPatchAttempts) return;
      this._patchAttempts += 1;
      setTimeout(() => this.ensureCartAddPatch(), 100);
    },

    bindCartAddPatchEvents() {
      if (window.__ellisRebuyAddPatchEvents) return;
      window.__ellisRebuyAddPatchEvents = true;

      const retry = () => this.ensureCartAddPatch();
      ['rebuy.ready', 'rebuy:smartcart.ready', 'rebuy:cart.ready', 'rebuy:smartcart.show'].forEach(
        (eventName) => {
          document.addEventListener(eventName, retry);
        }
      );
    },

    getLocale() {
      const locale = window.Shopify?.locale || document.documentElement.lang || 'en';
      return String(locale).split('-')[0].toLowerCase();
    },

    getTranslations() {
      const all = window.__ellisRebuyTranslations;
      if (!all) return null;
      const locale = this.getLocale();
      return all[locale] || all.en || null;
    },

    getSampleStrings() {
      return this.getTranslations()?.samples || null;
    },

    getWidgetStrings(widgetId) {
      const translations = this.getTranslations();
      if (!translations) return {};

      const global = translations.widget || {};
      const specific = translations[String(widgetId)] || {};
      return { ...global, ...specific };
    },

    isWidgetEvent(event, widgetId) {
      const widget = event?.detail?.widget;
      return widget && String(widget.id) === String(widgetId);
    },

    on(eventName, widgetId, callback) {
      document.addEventListener(eventName, (event) => {
        if (!this.isWidgetEvent(event, widgetId)) return;
        callback(event);
      });
    },
  },

  /**
   * Free samples — cart data, pricing, and progress helpers
   */
  Samples: {
    REBUY_MOUNT: 'rebuy',
    THEME_SELECTOR: '#sample-selector',
    CART_CACHE_MIN_MS: 4000,
    REBUY_SYNC_MIN_MS: 3000,
    _progressPricingItems: null,
    _progressSoonTimer: null,
    _progressFollowUpTimer: null,
    _cacheUpdatedAt: 0,
    _rebuySyncedAt: 0,
    _cachePromise: null,
    _rebuySyncPromise: null,
    _mountInProgress: false,
    _uiRefreshTimer: null,
    _uiRefreshInProgress: false,
    _uiRefreshQueued: false,
    _uiRefreshQueuedForce: false,
    _uiRefreshPendingForce: false,
    _uiRefreshDebounceTimer: null,
    _uiRefreshFollowUpTimer: null,
    _uiProgressDebounceTimer: null,
    _rebuyMountTimer: null,
    _rebuyMountAggressiveTimer: null,

    getItemProperty(item, key) {
      if (!item) return undefined;
      const props = item.properties;
      if (props) {
        if (Array.isArray(props)) {
          const match = props.find((entry) => entry && (entry.name === key || entry.key === key));
          if (match) return match.value;
        } else if (typeof props === 'object' && props[key] !== undefined) {
          return props[key];
        }
      }
      if (item.product?.properties && typeof item.product.properties === 'object') {
        return item.product.properties[key];
      }
      return item[key];
    },

    getRebuyCartItems() {
      const rebuy = window.Rebuy;
      if (!rebuy) return [];

      const candidates = [];

      if (typeof rebuy.SmartCart?.getCart === 'function') {
        try {
          const smartCart = rebuy.SmartCart.getCart();
          candidates.push(smartCart?.items, smartCart?.line_items);
        } catch {
          /* ignore */
        }
      }

      if (typeof rebuy.Cart?.getItems === 'function') {
        try {
          candidates.push(rebuy.Cart.getItems());
        } catch {
          /* ignore */
        }
      }

      candidates.push(
        rebuy.SmartCart?.cart?.items,
        rebuy.SmartCart?.items,
        rebuy.SmartCart?.cart?.line_items,
        rebuy.Cart?.cart?.items,
        rebuy.Cart?.items,
        rebuy.Cart?.cart?.line_items,
        rebuy.cart?.items
      );

      for (let i = 0; i < candidates.length; i += 1) {
        if (Array.isArray(candidates[i]) && candidates[i].length) return candidates[i];
      }

      const alpineItems = Utility.Alpine.getCartStore();
      if (alpineItems) {
        const storeItems = alpineItems.items ?? alpineItems.data?.items;
        if (Array.isArray(storeItems) && storeItems.length) return storeItems;
      }

      const cached = Utility.Cart._cache?.items;
      if (Array.isArray(cached) && cached.length) return cached;

      const fromScript = Utility.JSONScript.parse('cart-data', {});
      return Array.isArray(fromScript.items) ? fromScript.items : [];
    },

    getCartItems(cartMode) {
      if (cartMode === 'rebuy') return this.getRebuyCartItems();

      const cart = Utility.Alpine.getCartStore();
      if (!cart) {
        const fromScript = Utility.JSONScript.parse('cart-data', {});
        return Array.isArray(fromScript.items) ? fromScript.items : [];
      }

      const items = cart.items ?? cart.data?.items;
      return Array.isArray(items) ? items : [];
    },

    getItemVariantId(item) {
      return Number(item?.id ?? item?.variant_id ?? item?.variantId ?? 0);
    },

    getPaidItems(cartMode) {
      return this.getCartItems(cartMode).filter((item) => {
        if (!item) return false;
        if (Utility.Cart.isSampleItem(item) || Utility.Cart.isGiftItem(item)) return false;
        const quantity = Number(item.quantity ?? item.qty ?? 0);
        if (quantity <= 0) return false;
        return true;
      });
    },

    getSampleItems(cartMode) {
      return this.getCartItems(cartMode).filter((item) => {
        if (Utility.Cart.isSampleItem(item)) return true;
        const sample = this.getItemProperty(item, '_sample');
        return sample === true || sample === 'true';
      });
    },

    getSampleQuantityByVariant(cartMode) {
      const counts = {};
      this.getSampleItems(cartMode).forEach((item) => {
        const id = item.id ?? item.variant_id;
        counts[id] = (counts[id] || 0) + Number(item.quantity ?? 0);
      });
      return counts;
    },

    getTotalSampleQuantity(cartMode) {
      return this.getSampleItems(cartMode).reduce((sum, item) => sum + Number(item.quantity ?? 0), 0);
    },

    mergeSampleItemPricing(item, cachedItems = Utility.Cart._cache?.items) {
      const variantId = this.getItemVariantId(item);
      if (!variantId || !Array.isArray(cachedItems) || !cachedItems.length) return item;

      const cached = cachedItems.find((entry) => this.getItemVariantId(entry) === variantId);
      if (!cached) return item;

      const liveQty = Number(item.quantity ?? item.qty ?? 0);

      return {
        ...cached,
        ...item,
        quantity: liveQty,
        qty: liveQty,
      };
    },

    getProgressPricingSources() {
      const sources = [];

      if (Array.isArray(this._progressPricingItems)) {
        sources.push(...this._progressPricingItems);
      }

      if (Array.isArray(Utility.Cart._cache?.items)) {
        sources.push(...Utility.Cart._cache.items);
      }

      return sources;
    },

    mergeProgressItemPricing(liveItem, pricingSources = this.getProgressPricingSources()) {
      const variantId = this.getItemVariantId(liveItem);
      if (!variantId) return liveItem;

      const pricing = pricingSources.find((entry) => this.getItemVariantId(entry) === variantId);
      const liveQty = Number(liveItem.quantity ?? liveItem.qty ?? 0);

      if (!pricing) return liveItem;

      return {
        ...pricing,
        ...liveItem,
        quantity: liveQty,
        qty: liveQty,
      };
    },

    getProgressPricedSampleItems(cartMode) {
      const liveItems = this.getSampleItems(cartMode);
      const pricingSources = this.getProgressPricingSources();

      return liveItems.map((item) => this.mergeProgressItemPricing(item, pricingSources));
    },

    cacheHasShopifySamplePricing() {
      const items = Utility.Cart._cache?.items;
      if (!Array.isArray(items) || !items.length) return false;

      return items.some(
        (item) =>
          Utility.Cart.isSampleItem(item) &&
          (item.final_line_price !== undefined ||
            item.final_price !== undefined ||
            item.finalLinePrice !== undefined ||
            item.finalPrice !== undefined)
      );
    },

    getShopifyPricedSampleItems() {
      if (!this.cacheHasShopifySamplePricing()) return [];

      return Utility.Cart._cache.items.filter((item) => Utility.Cart.isSampleItem(item));
    },

    captureProgressPricingFromCache() {
      const items = Utility.Cart._cache?.items;
      if (!Array.isArray(items) || !items.length) return;

      const samples = items.filter((item) => Utility.Cart.isSampleItem(item));
      if (samples.length) {
        this._progressPricingItems = samples.map((item) => ({ ...item }));
      }
    },

    getProgressFreeSampleQuantity(cartMode, maxFree) {
      const maxFreeCount = maxFree || 2;
      const sampleItems = this.getSampleItems(cartMode);

      if (!sampleItems.length) return 0;

      const pricedItems = this.getProgressPricedSampleItems(cartMode);
      const hasExplicitPricing = pricedItems.some((item) => this.itemHasExplicitSamplePricing(item));

      if (hasExplicitPricing) {
        return pricedItems.reduce((sum, item) => {
          if (!this.isFreeSampleItem(item)) return sum;
          return sum + Number(item.quantity ?? item.qty ?? 0);
        }, 0);
      }

      const totalSamples = sampleItems.reduce(
        (sum, item) => sum + Number(item.quantity ?? item.qty ?? 0),
        0
      );

      const confirmedPaid = sampleItems.reduce((sum, item) => {
        if (!this.itemHasExplicitSamplePricing(item)) return sum;
        if (this.isFreeSampleItem(item)) return sum;
        return sum + Number(item.quantity ?? item.qty ?? 0);
      }, 0);

      if (confirmedPaid > 0) {
        return Math.max(0, Math.min(maxFreeCount, totalSamples - confirmedPaid));
      }

      return Math.min(maxFreeCount, totalSamples);
    },

    itemHasExplicitSamplePricing(item) {
      return (
        item?.final_price !== undefined ||
        item?.final_line_price !== undefined ||
        item?.finalPrice !== undefined ||
        item?.finalLinePrice !== undefined ||
        item?.discounted_price !== undefined ||
        item?.discountedPrice !== undefined
      );
    },

    isProgressFullyQualified(cartMode, maxFree) {
      const maxFreeCount = maxFree || 2;
      const freeQty = this.getProgressFreeSampleQuantity(cartMode, maxFreeCount);
      if (freeQty < maxFreeCount) return false;

      const shopifySamples = this.getShopifyPricedSampleItems();
      if (shopifySamples.length) {
        return this.samplesFullyQualified(cartMode, maxFreeCount);
      }

      return freeQty >= maxFreeCount;
    },

    getSampleItemsForPricing(cartMode) {
      return this.getProgressPricedSampleItems(cartMode);
    },

    getPickedSampleQuantity(cartMode, maxFree) {
      const maxFreeCount = maxFree || 2;
      return Math.min(maxFreeCount, this.getFreeSampleQuantity(cartMode));
    },

    getPendingProgressCount(cartMode, maxFree) {
      const maxFreeCount = maxFree || 2;
      const freeCount = this.getFreeSampleQuantity(cartMode);
      const remaining = Math.max(0, maxFreeCount - freeCount);
      return remaining > 0 ? remaining : maxFreeCount;
    },

    getSampleDiscountedUnitPrice(item) {
      if (!item) return null;
      const unitFields = [item.final_price, item.discounted_price, item.finalPrice, item.discountedPrice];
      for (let i = 0; i < unitFields.length; i += 1) {
        const raw = unitFields[i];
        if (raw === undefined || raw === null || raw === '') continue;
        const price = Number(raw);
        if (!Number.isNaN(price)) return price;
      }
      const quantity = Number(item.quantity ?? item.qty ?? 0);
      if (quantity <= 0) return null;
      const linePrice = Number(
        item.final_line_price ?? item.finalLinePrice ?? item.line_price ?? item.linePrice
      );
      if (!Number.isNaN(linePrice)) return linePrice / quantity;
      return null;
    },

    isFreeSampleItem(item) {
      if (!item) return false;
      const quantity = Number(item.quantity ?? item.qty ?? 0);
      if (quantity <= 0) return false;

      const discountedUnit = this.getSampleDiscountedUnitPrice(item);
      if (discountedUnit === 0) return true;
      if (discountedUnit !== null && discountedUnit > 0) return false;

      const linePrice = Number(
        item.final_line_price ?? item.finalLinePrice ?? item.line_price ?? item.linePrice
      );
      if (!Number.isNaN(linePrice) && linePrice === 0) return true;

      const originalUnit = Number(item.price ?? item.original_price ?? item.originalPrice);
      if (!Number.isNaN(originalUnit) && originalUnit === 0) return true;

      return false;
    },

    getFreeSampleQuantity(cartMode) {
      return this.getSampleItemsForPricing(cartMode).reduce((sum, item) => {
        if (!this.isFreeSampleItem(item)) return sum;
        return sum + Number(item.quantity ?? item.qty ?? 0);
      }, 0);
    },

    getPaidSampleQuantity(cartMode) {
      return this.getSampleItemsForPricing(cartMode).reduce((sum, item) => {
        if (this.isFreeSampleItem(item)) return sum;
        return sum + Number(item.quantity ?? item.qty ?? 0);
      }, 0);
    },

    areAllSamplesFree(cartMode) {
      const samples = this.getSampleItemsForPricing(cartMode);
      if (!samples.length) return false;
      return this.getPaidSampleQuantity(cartMode) === 0 && samples.every((item) => this.isFreeSampleItem(item));
    },

    samplesFullyQualified(cartMode, maxFree) {
      const maxFreeCount = maxFree || 2;
      if (this.getPaidSampleQuantity(cartMode) > 0) return false;
      return this.getFreeSampleQuantity(cartMode) >= maxFreeCount;
    },

    cartHasItems(cartMode) {
      return this.getCartItems(cartMode).some((item) => Number(item?.quantity ?? item?.qty ?? 0) > 0);
    },

    cartQualifiesForSamples(cartMode) {
      if (!this.cartHasItems(cartMode)) return false;
      if (this.getPaidItems(cartMode).length > 0) return true;
      if (cartMode !== 'rebuy') return false;
      const cached = Utility.Cart._cache?.items;
      if (!Array.isArray(cached) || !cached.length) return false;
      return cached.some(
        (item) =>
          item &&
          !Utility.Cart.isSampleItem(item) &&
          !Utility.Cart.isGiftItem(item) &&
          Number(item.quantity) > 0
      );
    },

    isCartReady(cartMode) {
      if (cartMode === 'rebuy') {
        return !!(window.Rebuy?.Cart || window.Rebuy?.SmartCart);
      }
      const cart = Utility.Alpine.getCartStore();
      if (!cart) return false;
      const items = cart.items ?? cart.data?.items;
      return Array.isArray(items);
    },

    hydrateCacheFromRebuy() {
      const rebuy = window.Rebuy;
      if (!rebuy) return false;

      const sources = [rebuy.SmartCart?.cart, rebuy.Cart?.cart];

      if (typeof rebuy.SmartCart?.getCart === 'function') {
        try {
          sources.push(rebuy.SmartCart.getCart());
        } catch {
          /* ignore */
        }
      }

      for (let i = 0; i < sources.length; i += 1) {
        const cart = sources[i];
        if (cart && Array.isArray(cart.items)) {
          Utility.Cart._cache = cart;
          Utility.Cart._cacheFetchedAt = Date.now();
          this._cacheUpdatedAt = Date.now();
          return true;
        }
      }

      return false;
    },

    isCacheStale() {
      return Date.now() - this._cacheUpdatedAt >= this.CART_CACHE_MIN_MS;
    },

    async refreshCache(options = {}) {
      const force = options.force === true;
      const now = Date.now();

      if (!force && this._cachePromise) return this._cachePromise;
      if (!force && now - this._cacheUpdatedAt < this.CART_CACHE_MIN_MS) return Promise.resolve();
      if (force && !this.isCacheStale() && Utility.Cart._cache) return Promise.resolve();

      this._cachePromise = Utility.Cart.fetch(true)
        .then(() => {
          this._cacheUpdatedAt = Date.now();
        })
        .catch(() => {
          Utility.Cart.clearCache();
        })
        .finally(() => {
          this._cachePromise = null;
        });

      return this._cachePromise;
    },

    async syncRebuyBeforeMount(options = {}) {
      const force = options.force === true;
      const now = Date.now();

      if (!force && this._rebuySyncPromise) return this._rebuySyncPromise;
      if (!force && now - this._rebuySyncedAt < this.REBUY_SYNC_MIN_MS) {
        if (this.hydrateCacheFromRebuy()) return Promise.resolve();
        if (!this.isCacheStale()) return Promise.resolve();
      }

      this._rebuySyncPromise = (async () => {
        const rebuyCart = window.Rebuy?.Cart;
        const needsRebuyFetch =
          rebuyCart &&
          typeof rebuyCart.fetchShopifyCart === 'function' &&
          (force || now - this._rebuySyncedAt >= this.REBUY_SYNC_MIN_MS);

        if (needsRebuyFetch) {
          await new Promise((resolve) => {
            rebuyCart.fetchShopifyCart(() => resolve());
          });
          this._rebuySyncedAt = Date.now();
        }

        if (!this.hydrateCacheFromRebuy()) {
          await this.refreshCache({ force: false });
        }
      })().finally(() => {
        this._rebuySyncPromise = null;
      });

      return this._rebuySyncPromise;
    },

    async addSample(variantId, cartMode) {
      const item = {
        id: variantId,
        quantity: 1,
        properties: { _sample: true },
      };

      await Utility.Cart.addItems([item]);
      this.captureProgressPricingFromCache();

      const rebuyCart = window.Rebuy?.Cart;
      if (rebuyCart && typeof rebuyCart.fetchShopifyCart === 'function') {
        await new Promise((resolve) => {
          rebuyCart.fetchShopifyCart(() => resolve());
        });
        this._rebuySyncedAt = Date.now();
      }

      if (cartMode === 'alpine') {
        await Utility.Alpine.syncCartFromRebuy({ skipRebuyFetch: true });
      }

      this.refreshSampleProgressSoon();
    },

    getMaxFreeSamples(root) {
      const value = parseInt(root?.dataset?.maxFreeSamples || '2', 10);
      return Number.isNaN(value) ? 2 : value;
    },

    formatSampleCount(text, count) {
      if (!text) return '';
      const value = String(count);
      return text.replace(/\{\{\s*count\s*\}\}/g, value).replace(/\{count\}/g, value);
    },

    applySampleTranslations(root) {
      const strings = Utility.Rebuy.getSampleStrings();
      if (!strings || !root) return;

      if (root.classList.contains('sample-progress')) {
        if (strings.pending_progress) {
          root.dataset.pendingMessage = strings.pending_progress;
          root.dataset.pendingMessageFallback = strings.pending_progress;
        }
        if (strings.qualified_progress) {
          root.dataset.qualifiedMessage = strings.qualified_progress;
          root.dataset.qualifiedMessageFallback = strings.qualified_progress;
        }
        return;
      }

      if (!root.classList.contains('sample-selector')) return;

      const datasetMap = {
        pickSamples: 'pick_two',
        pickOneMore: 'pick_one_more',
        allSelected: 'two_selected',
        qualifiedHeading: 'qualified_heading',
        freeLabel: 'free_label',
        selectPlaceholder: 'select_sample',
        addingLabel: 'adding',
        addedLabel: 'added',
        samplesNote: 'samples_note',
        soldOutLabel: 'sold_out',
      };

      Object.entries(datasetMap).forEach(([dataKey, stringKey]) => {
        if (strings[stringKey]) root.dataset[dataKey] = strings[stringKey];
      });

      const qualifiedTitle = root.querySelector('.sample-selector-qualified-title');
      const pickerStatus = root.querySelector('.sample-selector-picker-status');
      const freeLabelEl = root.querySelector('.sample-selector-free-label');
      // const disclaimer = root.querySelector('.sample-selector-disclaimer');
      const selectLabel = root.querySelector('.sample-select-label');

      if (qualifiedTitle && strings.qualified_heading) {
        qualifiedTitle.textContent = strings.qualified_heading;
      }
      if (pickerStatus && strings.pick_two) {
        pickerStatus.textContent = strings.pick_two;
      }
      if (freeLabelEl && strings.free_label) {
        freeLabelEl.textContent = strings.free_label;
      }
      // if (disclaimer && strings.disclaimer) {
      //   disclaimer.textContent = strings.disclaimer;
      // }
      if (selectLabel && strings.select_sample && root.dataset.initialized !== 'true') {
        selectLabel.textContent = strings.select_sample;
      }

      if (strings.sold_out) {
        root.dataset.soldOutLabel = strings.sold_out;
        root.querySelectorAll('.sample-select-option.is-sold-out .sample-option-sold-out').forEach((badge) => {
          badge.textContent = strings.sold_out;
        });
      }

      if (root.dataset.initialized === 'true') {
        if (typeof root._sampleRefreshUI === 'function') {
          root._sampleRefreshUI();
        }
      }
    },

    applyAllSampleTranslations() {
      Utility.DomSelector.$$('.sample-progress, .sample-selector').forEach((el) => {
        this.applySampleTranslations(el);
      });
    },

    getSourceSelector() {
      return (
        Utility.DomSelector.$('#free-samples-source .sample-selector') ||
        Utility.DomSelector.$(`${this.THEME_SELECTOR}:not([data-sample-mount])`)
      );
    },

    getSourceProgress() {
      return (
        Utility.DomSelector.$('#free-samples-source .sample-progress') ||
        Utility.DomSelector.$('#sample-progress:not([data-sample-mount])')
      );
    },

    isSampleProgressEnabled() {
      return false;
    },

    setVisible(el, visible) {
      if (!el) return;
      const shouldHide = !visible;
      if (el.hidden === shouldHide && el.classList.contains('hidden') === shouldHide) return;
      el.hidden = shouldHide;
      Utility.CSS.toggle(el, 'hidden', shouldHide);
    },

    getSampleProgressTemplate(kind, progress) {
      const strings = Utility.Rebuy.getSampleStrings();
      const translationKey = kind === 'qualified' ? 'qualified_progress' : 'pending_progress';
      if (strings?.[translationKey]) {
        return Utility.decodeHtmlEntities(strings[translationKey]);
      }

      const datasetKey = kind === 'qualified' ? 'qualifiedMessage' : 'pendingMessage';
      const fallbackKey = kind === 'qualified' ? 'qualifiedMessageFallback' : 'pendingMessageFallback';
      return Utility.decodeHtmlEntities(
        progress?.dataset?.[datasetKey] || progress?.dataset?.[fallbackKey] || ''
      );
    },

    getProgressMessage(progress, cartMode, maxFree) {
      if (!progress) return '';
      const maxFreeCount = maxFree || this.getMaxFreeSamples(progress);

      if (this.isProgressFullyQualified(cartMode, maxFreeCount)) {
        return this.getSampleProgressTemplate('qualified', progress);
      }

      const template = this.getSampleProgressTemplate('pending', progress);
      const freeQty = this.getProgressFreeSampleQuantity(cartMode, maxFreeCount);
      const remaining = Math.max(0, maxFreeCount - freeQty);
      const count = remaining > 0 ? remaining : maxFreeCount;
      return this.formatSampleCount(template, count);
    },

    getDefaultProgressMessage(progress, maxFree, cartMode = 'alpine') {
      const maxFreeCount = maxFree || this.getMaxFreeSamples(progress);
      const template = this.getSampleProgressTemplate('pending', progress);
      const freeQty = this.getProgressFreeSampleQuantity(cartMode, maxFreeCount);
      const remaining = Math.max(0, maxFreeCount - freeQty);
      const count = remaining > 0 ? remaining : maxFreeCount;
      return this.formatSampleCount(template, count);
    },

    updateProgress(progress, cartMode, maxFree, options = {}) {
      if (!progress) return;

      if (!this.isSampleProgressEnabled()) {
        this.setVisible(progress, false);
        return;
      }

      const maxFreeCount = maxFree || this.getMaxFreeSamples(progress);
      const qualifies = options.qualifies === true;
      const hasItems = options.hasItems === true;
      const freeSampleQuantity =
        typeof options.freeSampleQuantity === 'number'
          ? options.freeSampleQuantity
          : this.getProgressFreeSampleQuantity(cartMode, maxFreeCount);
      const filledCount = Math.min(freeSampleQuantity, maxFreeCount);
      const fullyQualified = this.isProgressFullyQualified(cartMode, maxFreeCount);

      this.setVisible(progress, hasItems);
      if (!hasItems) return;

      const progressMessage = progress.querySelector('.sample-progress-message');
      const fill = progress.querySelector('.sample-progress__fill');

      if (progressMessage) {
        let message = '';
        if (qualifies && hasItems) {
          message = fullyQualified
            ? this.getSampleProgressTemplate('qualified', progress)
            : this.getProgressMessage(progress, cartMode, maxFreeCount);
        } else {
          message = this.getDefaultProgressMessage(progress, maxFreeCount, cartMode);
        }

        progressMessage.textContent = message;
        progressMessage.hidden = !message;
      }

      if (fill) {
        const progressPercent = maxFreeCount > 0 ? (filledCount / maxFreeCount) * 100 : 0;
        fill.style.width = filledCount > 0 ? `${Math.min(100, progressPercent)}%` : '0%';
      }
    },

    refreshAllProgressElements() {
      Utility.DomSelector.$$('.sample-progress').forEach((progressEl) => {
        const cartMode = progressEl.dataset.sampleMount === this.REBUY_MOUNT ? 'rebuy' : 'alpine';
        const cartReady = this.isCartReady(cartMode);
        const hasItems = cartReady && this.cartHasItems(cartMode);
        const qualifies = cartReady && this.cartQualifiesForSamples(cartMode);
        const maxFree = this.getMaxFreeSamples(progressEl);
        const freeSampleQuantity = cartReady ? this.getProgressFreeSampleQuantity(cartMode, maxFree) : 0;

        this.updateProgress(progressEl, cartMode, maxFree, {
          qualifies,
          hasItems,
          freeSampleQuantity,
        });
      });
    },

    refreshSampleProgressOnly() {
      this.refreshAllProgressElements();
    },

    refreshSampleProgressSoon() {
      clearTimeout(this._progressSoonTimer);
      clearTimeout(this._progressFollowUpTimer);

      this.refreshSampleProgressOnly();

      this._progressSoonTimer = setTimeout(() => {
        this._progressSoonTimer = null;
        this.refreshSampleProgressOnly();
      }, 200);

      this._progressFollowUpTimer = setTimeout(() => {
        this._progressFollowUpTimer = null;
        this.refreshSampleProgressOnly();
      }, 550);
    },

    bindSampleProgressQtyWatcher() {
      if (window.__ellisSampleProgressQtyWatcher) return;
      window.__ellisSampleProgressQtyWatcher = true;

      const attach = () => {
        const rebuyCart =
          document.querySelector('#rebuy-cart, #rebuy-smart-cart, .rebuy-cart.is-visible') ||
          document.querySelector('.rebuy-cart, .rebuy-smart-cart');

        const cartItems = rebuyCart?.querySelector(
          '[data-rebuy-component="cart-items"], .rebuy-cart__flyout-items, .rebuy-cart__flyout-item-list'
        );

        if (!cartItems || cartItems.__ellisSampleProgressQtyObserver) return;
        cartItems.__ellisSampleProgressQtyObserver = true;

        const onMutation = Utility.debounce(() => this.refreshSampleProgressSoon(), 120);

        new MutationObserver(onMutation).observe(cartItems, {
          childList: true,
          subtree: true,
          characterData: true,
          attributes: true,
        });
      };

      ['rebuy:smartcart.show', 'rebuy:cart.show', 'rebuy:cart.ready', 'rebuy:smartcart.ready'].forEach(
        (eventName) => {
          document.addEventListener(eventName, attach);
        }
      );

      attach();
    },

    refreshSelectorUIs() {
      Utility.DomSelector.$$('.sample-selector[data-initialized="true"]').forEach((root) => {
        if (typeof root._sampleRefreshUI === 'function') {
          root._sampleRefreshUI();
        }
      });
    },

    refreshProgressOnly() {
      this.refreshSampleProgressOnly();
      this.refreshSelectorUIs();
    },

    async runUiRefresh(options = {}) {
      const forceCache = options === true || options?.forceCache === true;

      if (this._uiRefreshInProgress) {
        if (forceCache) this._uiRefreshQueuedForce = true;
        else this._uiRefreshQueued = true;
        return;
      }

      this._uiRefreshInProgress = true;

      try {
        if (forceCache && this.isCacheStale()) {
          if (window.Rebuy?.Cart) {
            await this.syncRebuyBeforeMount({ force: false });
          } else {
            await this.refreshCache({ force: false });
          }
        }

        this.refreshSelectorUIs();
        this.refreshAllProgressElements();
      } finally {
        this._uiRefreshInProgress = false;

        if (this._uiRefreshQueuedForce) {
          this._uiRefreshQueuedForce = false;
          this.runUiRefresh({ forceCache: true });
        } else if (this._uiRefreshQueued) {
          this._uiRefreshQueued = false;
          this.runUiRefresh({ forceCache: false });
        }
      }
    },

    scheduleUiRefresh(options = {}) {
      const forceCache = options.forceCache === true;
      const delay = typeof options.delay === 'number' ? options.delay : 300;

      if (forceCache) this._uiRefreshPendingForce = true;

      clearTimeout(this._uiRefreshDebounceTimer);
      this._uiRefreshDebounceTimer = setTimeout(() => {
        this._uiRefreshDebounceTimer = null;
        const shouldForce = this._uiRefreshPendingForce;
        this._uiRefreshPendingForce = false;
        this.runUiRefresh({ forceCache: shouldForce });
      }, delay);
    },

    scheduleProgressRefresh(delay = 150) {
      clearTimeout(this._uiProgressDebounceTimer);
      this._uiProgressDebounceTimer = setTimeout(() => {
        this._uiProgressDebounceTimer = null;
        this.refreshProgressOnly();
      }, delay);
    },

    bindAlpineCartWatcher() {
      if (window.__ellisAlpineCartSampleWatcher) return;

      const cart = Utility.Alpine.getCartStore();
      if (!cart || !window.Alpine?.effect) return;

      window.__ellisAlpineCartSampleWatcher = true;

      window.Alpine.effect(() => {
        const items = cart.items ?? cart.data?.items ?? [];
        const itemCount = items.length;
        const updating = !!cart.updating;

        if (!this._alpineCartWatcherPrimed) {
          this._alpineCartWatcherPrimed = true;
          this._alpineCartWatcherItems = itemCount;
          this._alpineCartWatcherUpdating = updating;
          return;
        }

        const itemsChanged = itemCount !== this._alpineCartWatcherItems;
        const finishedUpdate = this._alpineCartWatcherUpdating && !updating;

        this._alpineCartWatcherItems = itemCount;
        this._alpineCartWatcherUpdating = updating;

        if (finishedUpdate || itemsChanged) {
          this.scheduleProgressRefresh();
        }
      });
    },

    patchCartFetchRefresh() {
      if (window.__ellisSampleCartFetchPatch) return;
      window.__ellisSampleCartFetchPatch = true;

      const nativeFetch = window.fetch.bind(window);

      window.fetch = async function ellisSampleCartFetch(input, init) {
        const response = await nativeFetch(input, init);

        try {
          const url = typeof input === 'string' ? input : input?.url || '';
          const method = String(init?.method || 'GET').toUpperCase();

          if (method === 'POST' && /\/cart\/(add|change|update|clear)\.js/.test(url)) {
            Utility.Samples?.refreshSampleProgressSoon?.();
          }
        } catch {
          /* ignore */
        }

        return response;
      };
    },

    bindCartWatchers() {
      if (window.__ellisSampleCartWatchers) return;
      window.__ellisSampleCartWatchers = true;

      const refreshProgress = () => this.refreshSampleProgressSoon();

      ['rebuy:cart.change', 'rebuy.add', 'rebuy:smartcart.show'].forEach((eventName) => {
        document.addEventListener(eventName, refreshProgress);
      });

      document.addEventListener('rebuy:cart.show', () => {
        this.refreshSampleProgressSoon();
      });

      document.addEventListener('alpine:initialized', () => this.bindAlpineCartWatcher());
      this.bindAlpineCartWatcher();
      this.bindSampleProgressQtyWatcher();
      this.patchCartFetchRefresh();
    },
  },

  /**
   * Limit the number of times a function is called within defined timeout
   * @param {function} fn - function to execute
   * @param {integer} wait - Timeout to fire event
   * @returns {Function} Debounced function with optional cancel() method
   */
  debounce(fn, wait) {
    let t;
    const debounced = (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
    debounced.cancel = () => {
      clearTimeout(t);
      t = null;
    };
    return debounced;
  },

  /**
   * Throttle function calls
   * @param {function} fn
   * @param {number} wait
   * @returns {Function}
   */
  throttle(fn, wait) {
    let last = 0;
    let timer;
    return function throttled(...args) {
      const now = Date.now();
      const remaining = wait - (now - last);
      if (remaining <= 0) {
        last = now;
        fn.apply(this, args);
      } else if (!timer) {
        timer = setTimeout(() => {
          last = Date.now();
          timer = null;
          fn.apply(this, args);
        }, remaining);
      }
    };
  },

  /**
   * Run callback when DOM is ready
   * @param {Function} callback
   */
  whenReady(callback) {
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', callback);
    } else {
      callback();
    }
  },

  /**
   * Format date
   * @param {string} value
   * @return {String} formatted value
   */
  formatDate(value) {
    const date = new Date(value);
    const ye = new Intl.DateTimeFormat('en', { year: 'numeric' }).format(date);
    const mo = new Intl.DateTimeFormat('en', { month: 'short' }).format(date);
    const da = new Intl.DateTimeFormat('en', { day: '2-digit' }).format(date);
    return `${mo} ${da} ${ye}`;
  },

  /**
   * Decode HTML entities in a string
   * @param {string} text
   * @returns {string}
   */
  decodeHtmlEntities(text) {
    if (!text) return '';
    const el = document.createElement('textarea');
    el.innerHTML = text;
    return el.value;
  },

  /**
   * Simple {{ key }} template replacement
   * @param {string} template
   * @param {Object} values
   * @returns {string}
   */
  formatTemplate(template, values = {}) {
    if (!template) return '';
    return String(template).replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
      return values[key] !== undefined && values[key] !== null ? String(values[key]) : '';
    });
  },

  /**
   * Get swatch hex color code or image url
   * @param {string} colorName
   * @returns {string|false} background style
   */
  getSwatchStyle(colorName) {
    if (!this._swatchesCache) {
      const swatchesElement = Utility.DomSelector.$('[data-swatches-colorlist-json]');
      if (swatchesElement) {
        try {
          this._swatchesCache = JSON.parse(swatchesElement.innerText);
        } catch (e) {
          console.warn('Failed to parse swatches color list', e);
          this._swatchesCache = {};
        }
      } else {
        this._swatchesCache = {};
      }
    }

    if (!this._swatchesCache || Object.keys(this._swatchesCache).length === 0) {
      return false;
    }

    const key = String(colorName).replace(/-|\s/g, '').toLowerCase();
    const swatch = this._swatchesCache[key];
    if (typeof swatch !== 'undefined') {
      if (swatch.match(/\.(jpeg|jpg|png|gif)/g) != null) {
        return `background-image: url(${swatch}); background-size: cover; background-position: center;`;
      }
      return `background-color: ${swatch}`;
    }
    return false;
  },

  /**
   * Toggle element visibility
   * @param {Element} container - Container element
   * @param {string} activity - Activity to toggle ('open' | other)
   */
  toggleElement(container, activity) {
    const contentBlock =
      container.querySelector('[data-type="content"]') ||
      container.querySelector('.toggle-content');
    if (!contentBlock) return;

    if (activity == 'open') {
      Utility.CSS.add([contentBlock, container], 'open');
      contentBlock.removeAttribute('inert');
      contentBlock.setAttribute('aria-hidden', 'false');
    } else {
      Utility.CSS.remove([contentBlock, container], 'open');
      contentBlock.setAttribute('inert', '');
      contentBlock.setAttribute('aria-hidden', 'true');
    }
  },

  /**
   * Live Region Manager - Persistent ARIA live regions for screen reader announcements
   * @private
   */
  _liveRegions: {},

  /**
   * Get or create live region
   * @private
   */
  _getLiveRegion(priority) {
    if (!this._liveRegions[priority]) {
      const region = document.createElement('div');
      region.setAttribute('aria-live', priority);
      region.setAttribute('aria-atomic', 'true');
      region.className = 'visually-hidden';
      document.body.appendChild(region);
      this._liveRegions[priority] = region;
    }
    return this._liveRegions[priority];
  },

  /**
   * Announce message to screen readers
   * @param {string} message - Message to announce
   * @param {string} priority - 'polite' or 'assertive' (default: 'polite')
   */
  announceToScreenReader(message, priority = 'polite') {
    const region = this._getLiveRegion(priority);

    region.textContent = '';

    setTimeout(() => {
      region.textContent = message;
    }, 100);
  },

  /**
   * Resolve shop money format string
   * @param {string} [format]
   * @returns {string}
   */
  getMoneyFormat(format) {
    return (
      format ||
      window.NewThink?.MONEY_FORMAT ||
      globalThis.themeData?.money_format ||
      '${{ amount }}'
    );
  },

  /**
   * Format money values based on your shop currency settings
   * @param {Number|string} cents - value in cents or dollar amount
   * @return {String} formatted value
   */
  formatMoney(cents, format) {
    format = Utility.getMoneyFormat(format);
    if (typeof cents === 'string') cents = cents.replace('.', '');
    const numericCents = Number(cents);

    const formatWithDelimiters = (num, prec = 2, thou = ',', dec = '.') => {
      if (!Number.isFinite(num)) {
        const zero = (0).toFixed(prec);
        const zparts = zero.split('.');
        return zparts[0] + (zparts[1] ? dec + zparts[1] : '');
      }
      const fixed = (num / 100).toFixed(prec);
      const parts = fixed.split('.');
      return parts[0].replace(/(\d)(?=(\d{3})+(?!\d))/g, `$1${thou}`) + (parts[1] ? dec + parts[1] : '');
    };

    const match = format.match(/\{\{\s*(\w+)\s*\}\}/);
    if (!match) {
      return formatWithDelimiters(numericCents, 2);
    }

    const type = match[1];
    const value = {
      amount: formatWithDelimiters(numericCents, 2),
      amount_no_decimals: formatWithDelimiters(numericCents, 0),
      amount_with_comma_separator: formatWithDelimiters(numericCents, 2, '.', ','),
      amount_no_decimals_with_comma_separator: formatWithDelimiters(numericCents, 0, '.', ','),
    }[type];

    const resolved = value !== undefined ? value : formatWithDelimiters(numericCents, 2);
    return format.replace(/\{\{\s*\w+\s*\}\}/, resolved);
  },

  /**
   * Formatted money as plain text (shop money_format often wraps HTML; use for buttons / aria).
   * @param {Number|string} cents
   * @param {string} [format]
   * @returns {string}
   */
  formatMoneyText(cents, format) {
    const raw = Utility.formatMoney(cents, format);
    if (typeof document === 'undefined' || !raw) return raw;
    const el = document.createElement('div');
    el.innerHTML = raw;
    const text = el.textContent?.trim();
    return text || raw;
  },

  /**
   * Extract error message from various error formats
   * @param {Error|Object|string} error - Error object or message
   * @returns {string} Extracted error message
   */
  extractErrorMessage(error) {
    if (typeof error === 'string') {
      return error.trim() || 'An error occurred';
    }

    if (error?.data) {
      const data =
        typeof error.data === 'string'
          ? (() => {
              try {
                return JSON.parse(error.data);
              } catch {
                return { message: error.data };
              }
            })()
          : error.data;

      return (
        data?.message ||
        data?.description ||
        data?.error ||
        data?.errors ||
        'An error occurred'
      );
    }

    if (error?.message) {
      return error.message;
    }

    return 'An error occurred';
  },

  handleError(e, title = 'Error') {
    const message = this.extractErrorMessage(e);
    if (window.notificationEle?.updateNotification) {
      window.notificationEle.updateNotification(title, message, {
        type: 'error',
        timeout: 15000,
      });
    } else {
      console.error(`[${title}]`, message);
    }
  },

  handleSuccess(e, title = 'Success') {
    if (window.notificationEle?.updateNotification) {
      window.notificationEle.updateNotification(title, e, {
        type: 'success',
        timeout: 15000,
      });
    }
  },
};

window.Utility = Utility;
window.TRAP_FOCUS_HANDLERS = TRAP_FOCUS_HANDLERS;